import Check from "@mui/icons-material/Check";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Button } from "@mui/material";
import type { ReactNode } from "react";

// Presentation-only paged content shell. Renders title + subtitle, a themeable
// horizontal rule, the active page's content, page-dot indicators, and
// prev/next (or finish) buttons. The component owns NO dialog/popover surface
// — callers supply that. TrainingModal wraps it inside a bottom-right popover;
// ArtifactLightbox wraps it inside a centered <CustomModal>.

export interface PagedViewPage {
  title: string;
  /** Per-page subtitle. Falls back to "{n} of {total}" when omitted. */
  subtitle?: string;
  content: ReactNode;
}

export interface PagedViewProps {
  pages: PagedViewPage[];
  pageIndex: number;
  onPageChange: (next: number) => void;
  /** Called when the last-page action button is clicked. Render the finish action only when this is provided. */
  onFinish?: () => void;
  /** Label for the last-page action button. Defaults to "Done". */
  finishLabel?: string;
  /** Color of the horizontal rule beneath the title. Defaults to TrainingModal yellow. */
  ruleColor?: string;
  /** id attribute on the title h2 (for aria-labelledby on the outer dialog). */
  titleId?: string;
  /** Content rendered to the right of the title — typically a close button when the outer container needs one. */
  headerEndSlot?: ReactNode;
  /** Content rendered at the right end of the footer, after Prev/Next/Finish — e.g. a Close button. */
  footerEndSlot?: ReactNode;
  /** Additional class names on the root wrapper. */
  className?: string;
  /**
   * Class names applied to the content wrapper (the div that hosts {page.content}).
   * Pass "flex-1 min-h-0" to make the content fill remaining vertical space —
   * useful for image lightboxes where you want the footer pinned to the bottom.
   * When set, the default `minHeight: 280` style is dropped.
   */
  contentClassName?: string;
  /**
   * When true, Previous and Next buttons are rendered on every page and become
   * `disabled` at the boundaries instead of disappearing. Reduces layout shift
   * across pages. Default: false (legacy TrainingModal behavior — buttons hide
   * at the boundaries).
   */
  keepNavButtonsVisible?: boolean;
  /**
   * Narrow-container variant: tightens the 40px gutters to 20px and drops the
   * display-size title to a panel-size one. For hosts ~344px wide (the oraChat
   * side panel rail) where the default popover metrics would wrap. Default
   * false — every existing consumer renders unchanged.
   */
  dense?: boolean;
}

const navButtonBaseSx = {
  textTransform: "none",
  color: "#182230",
  fontFamily: "Roboto",
  fontSize: "0.875rem",
  fontWeight: 400,
  lineHeight: "1.25rem",
  borderRadius: "0.5rem",
  padding: "0.63rem 0.88rem",
  boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
} as const;

const navButtonPrimarySx = {
  ...navButtonBaseSx,
  border: "1px solid #FD0",
  backgroundColor: "#FFC600",
  "&:hover": {
    backgroundColor: "#e6b200",
    border: "1px solid #FD0",
    boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
  },
} as const;

const navButtonSecondarySx = {
  ...navButtonBaseSx,
  backgroundColor: "#FFF",
  "&:hover": {
    backgroundColor: "#F9FAFB",
    boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
  },
} as const;

export default function PagedView({
  pages,
  pageIndex,
  onPageChange,
  onFinish,
  finishLabel = "Done",
  ruleColor = "#FFC400",
  titleId,
  headerEndSlot,
  footerEndSlot,
  className,
  contentClassName,
  keepNavButtonsVisible = false,
  dense = false,
}: PagedViewProps) {
  const page = pages[pageIndex];
  if (!page) return null;

  const gutter = dense ? "px-5" : "px-10";
  const ruleGutter = dense ? "mx-5" : "mx-10";
  const titleClass = dense
    ? "m-0 text-[1.5rem] font-normal leading-[2rem] tracking-[-0.03rem] text-[#FFC600]"
    : "m-0 text-[2.25rem] font-normal leading-[2.75rem] tracking-[-0.045rem] text-[#FFC600]";
  const subtitleClass = dense
    ? "mt-0.5 text-[0.9375rem] font-light leading-normal text-[#101828]"
    : "mt-0.5 text-[1.125rem] font-light leading-normal text-[#101828]";

  const totalPages = pages.length;
  const isFirst = pageIndex === 0;
  const isLast = pageIndex === totalPages - 1;
  const subtitle = page.subtitle ?? `${pageIndex + 1} of ${totalPages}`;
  // On the last page, the finish action (when provided) replaces the Next button.
  const showFinish = isLast && onFinish !== undefined;
  const showPrev = keepNavButtonsVisible || !isFirst;
  const showNext = !showFinish && (keepNavButtonsVisible || !isLast);

  return (
    <div className={className}>
      {/* Header */}
      <div
        className={`${gutter} ${dense ? "pt-5" : "pt-8"} pb-0 flex items-start justify-between`}
      >
        <div>
          <h2 id={titleId} className={titleClass}>
            {page.title}
          </h2>
          <div className={subtitleClass}>{subtitle}</div>
        </div>
        {headerEndSlot}
      </div>

      {/* Rule */}
      <div
        className={`${ruleGutter} mt-3 rounded-full`}
        style={{ height: 3, backgroundColor: ruleColor }}
      />

      {/* Content */}
      <div
        className={`${gutter} ${dense ? "py-4" : "py-5"} overflow-y-auto ${contentClassName ?? ""}`.trim()}
        style={contentClassName ? undefined : { minHeight: 280 }}
      >
        {page.content}
      </div>

      {/* Footer */}
      <div
        className={`${gutter} ${dense ? "py-3" : "py-4"} border-primary-gray-light-200 flex items-center justify-between`}
      >
        {/* Dot indicators */}
        <div className="flex items-center gap-1.5">
          {pages.map((_, i) => (
            <button
              key={i}
              onClick={() => onPageChange(i)}
              aria-label={`Go to page ${i + 1}`}
              className="rounded-full border-0 p-0 cursor-pointer transition-all duration-200"
              style={{
                width: i === pageIndex ? 20 : 8,
                height: 8,
                backgroundColor: i === pageIndex ? "#FFC400" : "#D0D5DD",
              }}
            />
          ))}
        </div>

        {/* Navigation buttons + optional footer-end slot */}
        <div className="flex gap-2 items-center">
          {showPrev && (
            <Button
              variant="contained"
              startIcon={<ChevronLeft />}
              onClick={() => onPageChange(pageIndex - 1)}
              disabled={isFirst}
              sx={navButtonSecondarySx}
            >
              Previous
            </Button>
          )}
          {showNext && (
            <Button
              variant="contained"
              endIcon={<ChevronRight />}
              onClick={() => onPageChange(pageIndex + 1)}
              disabled={isLast}
              sx={navButtonPrimarySx}
            >
              Next
            </Button>
          )}
          {showFinish && (
            <Button
              variant="contained"
              endIcon={<Check />}
              onClick={onFinish}
              sx={navButtonPrimarySx}
            >
              {finishLabel}
            </Button>
          )}
          {footerEndSlot}
        </div>
      </div>
    </div>
  );
}
